import React, { useState, useMemo, useEffect } from 'react';
import { StyledSelect } from '../StyledSelect';
import { ButtonBar } from '../ButtonBar';

import styles from './switchable.module.scss';

import { AnimatedGuide } from '../AnimatedGuide';
import { AthleteProfile } from '../AthleteProfile';
import { CheckpointResults } from '../CheckpointResults';
import { HeadToHead } from '../HeadToHead';
import { ImageWithDropDown } from '../ImageWithDropDown';
import { JudgesHeadToHead } from '../JudgesHeadToHead';
import { JudgesResults } from '../JudgesResults';
import { KnockOutTree2 } from '../KnockOutTree2';
import { KnockOutTree } from '../KnockOutTree';
import { Results } from '../Results';
import { SimpleTrack } from '../SimpleTrack';
import { Wipe } from '../Advertisement/wipe';
import { ComparisonLite } from '../ComparisonLite';
import { AthleteProfileLite } from '../AthleteProfileLite';
import { InteractiveTrack } from '../InteractiveTrack';
import { getTrans } from '../../Utils/translate';
import { off, on } from '../../Utils/events';
import { findByMatchingProperties } from '../../Utils/array';
import { dataFetcher } from '../../Utils/dataFetcher';

const SwitchableModules = ({ config }) => {
  const [option, setOption] = useState({ index: 0, type: config.modules[0].type });
  const [loading, setLoading] = useState(true);
  const allComponents = {
    animatedguide: AnimatedGuide,
    athleteprofile: AthleteProfile,
    checkpointresults: CheckpointResults,
    headtohead: HeadToHead,
    imagewithdropdown: ImageWithDropDown,
    athleteprofilelite: AthleteProfileLite,
    interactiveTrack: InteractiveTrack,
    judgesheadtohead: JudgesHeadToHead,
    comparisonlite: ComparisonLite,
    judgesresults: JudgesResults,
    knockouttree2: KnockOutTree2,
    knockouttree: KnockOutTree,
    results: Results,
    simpletrack: SimpleTrack,
    switchablemodules: SwitchableModules,
    wipe: Wipe
  };
  const CurrentComponent = useMemo(() => {
    let openWidget = option.type.toLowerCase();
    if (openWidget === 'judges-results') openWidget = 'judgesresults';
    return allComponents[openWidget];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [option]);

  const showAllComponents = () => {
    return (
      <>
        {config.modules.map((moduleConfig) => {
          let openWidget = moduleConfig.type.toLowerCase();

          if (openWidget === 'judges-results') openWidget = 'judgesresults';

          const Component = allComponents[openWidget];

          return (
            <div
              key={`module-${moduleConfig.type.toLowerCase()}-${moduleConfig.navigationTitle.toLowerCase()}`}
              className={styles.switchableModules}
            >
              <Component
                config={{
                  ...moduleConfig,
                  trackingTitle: `${
                    moduleConfig.trackingTitle || moduleConfig.navigationTitle
                  } - ${config.navigationTitle}`
                }}
              />
            </div>
          );
        })}
      </>
    );
  };

  const handleChange = (data) => {
    setOption({ type: config.modules[data.value].type, index: data.value });
  };

  const handleChangeButtonBar = (data) => {
    setOption({ type: config.modules[data].type, index: data });
  };

  const titles = config.modules.map(({ navigationTitle }, index) => {
    return { label: getTrans(navigationTitle) || navigationTitle, value: index };
  });

  // change nested component by type, based on events
  const onSwitchComponent = (event) => {
    // find modules with matching conditions
    let matches = [];
    event.detail.targets.map((_module) => {
      const _matches = findByMatchingProperties(config.modules, _module.condition);
      if (_matches.length) {
        matches.push({
          modules: _matches,
          config: _module.config
        });
      }
      return true;
    });
    // use first match
    const index = matches.length > 0 ? config.modules.indexOf(matches[0].modules[0]) : -1;
    if (index > -1) {
      // override config to set eg. "initiateWithAthleteId"
      config.modules[index] = { ...config.modules[index], ...matches[0].config };
      // change module in frontend
      handleChange({ value: index });
    }
  };

  useEffect(() => {
    if (config.hasOwnProperty('uriActiveEvent')) {
      dataFetcher(config.uriActiveEvent).then((data) => {
        if (
          !data.error &&
          data.hasOwnProperty('event') &&
          config.modules[0].eventId === data.event.id
        ) {
          setOption((state) => {
            return { ...state, index: 0 };
          });
          setLoading(false);
        } else if (
          !data.error &&
          data.hasOwnProperty('event') &&
          config.modules[1].eventId === data.event.id
        ) {
          setOption((state) => {
            return { ...state, index: 1 };
          });
          setLoading(false);
        } else {
          setOption((state) => {
            return { ...state, index: 0 };
          });
          setLoading(false);
        }
      });
    } else {
      setOption((state) => {
        return { ...state, index: 0 };
      });
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // listen to component switch events
    on('switchComponent', onSwitchComponent);
    return () => {
      off('switchComponent', onSwitchComponent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className={styles.switchableModules}>
        {config.useList ? (
          ''
        ) : config.modules.length === 1 ? (
          // if there is just one list make a title
          <h3 className="h3">{getTrans(config.modules[0].navigationTitle)}</h3>
        ) : config.modules.length > 2 ? (
          // if there are more then two lists use StyledSelect
          <div className={styles.containerUi}>
            <StyledSelect
              className="result-select single"
              options={titles}
              selectFirst
              onChange={handleChange}
              value={{ label: titles[option.index].label, value: option.index }}
              maxHeight={400}
            />
          </div>
        ) : (
          // if there are just two lists use ButtonBar
          <div className={styles.containerUi}>
            {!loading && (
              <ButtonBar
                buttons={config.modules.map((item, index) => {
                  return {
                    text: item.navigationTitle,
                    active: option.index === index || (index === 0 && !option.index),
                    index: item.type
                  };
                })}
                onClick={handleChangeButtonBar}
              />
            )}
          </div>
        )}
      </div>

      {config.useList
        ? showAllComponents()
        : config.modules.hasOwnProperty(option.index) && (
            <CurrentComponent
              key={`module-${config.modules[
                option.index
              ].type.toLowerCase()}-${config.modules[
                option.index
              ].navigationTitle.toLowerCase()}`}
              config={{
                ...config.modules[option.index],
                trackingTitle: `${
                  config.modules[option.index].trackingTitle ||
                  config.modules[option.index].navigationTitle
                } - ${config.navigationTitle}`
              }}
            />
          )}
    </>
  );
};

export { SwitchableModules };
